import { Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { debounce } from 'lodash-es';
import PerfectScrollbar from 'perfect-scrollbar';
import React from 'react';

import { AdviserSearchApi } from '~/api/adviser-search-api';
import { EmployerSearchApi } from '~/api/employer-search-api';
import { InvestmentManagerSearchApi } from '~/api/investment-manager-search-api';
import { MemberSearchApi, MemberSearchResult } from '~/api/member-search-api';
import { ContextMenuButton } from '~/components/context-menu-button/context-menu-button';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import {
	AdvancedSearchAutocompleteProps,
	AdvancedSearchInputProps,
	AdvancedSearchInputType,
} from '~/components/entity-search/advanced-search-input';
import { EntitySearchAutocomplete } from '~/components/entity-search/entity-search-autocomplete/entity-search-autocomplete';
import { EntitySearchDatepicker } from '~/components/entity-search/entity-search-datepicker/entity-search-datepicker';
import { EntitySearchInput } from '~/components/entity-search/entity-search-input/entity-search-input';
import { EntitySearchMainDetails } from '~/components/entity-search/entity-search-main-details/entity-search-main-details';
import { EntitySearchResult } from '~/components/entity-search/entity-search-result/entity-search-result';
import { EntitySearchSupportingDetailsItem } from '~/components/entity-search/entity-search-supporting-details-item/entity-search-supporting-details-item';
import {
	EntityIconClass,
	EntitySearchStatus,
	EntitySearchType,
} from '~/components/entity-search/entity-type';
import { EntityTypeUtils } from '~/components/entity-search/entity-type-util';
import {
	SearchDefinition,
	SearchDefinitionMap,
	SearchType,
} from '~/components/field/common/search-definition';
import { Notification } from '~/components/notification/notification';
import { CommonURLParam } from '~/constants';
import { AutocompleteFieldModel } from '~/models/autocomplete-field-model';
import { EntityType } from '~/models/entities/entity-type';
import { FetchUtils } from '~/utils/fetch-utils';
import { FocusTrap } from '~/utils/focus-trap';
import { Key } from '~/utils/key';
import { Logger } from '~/utils/logger';

import styles from '~/components/entity-search/entity-search.module.scss';
import { SettingsState } from '~/settings-state/settings-state';
import utilStyles from '~/styles/util.module.scss';
import { AcurityFieldUtils } from '~/utils/acurity-field-utils';
import { AutocompleteColumnHeader } from '../field/common/autocomplete/autocomplete-react';
import { ApexIconReact } from '~/components/apex-icon/apex-icon-react';

interface Props {
	urlParams: { [param: string]: string };
	entityType: EntityType;
	/**
	 * entityDisplayName maps to Entity.displayName
	 */
	entityDisplayName: string;
	searchHistoryList: EntitySearchType[];
	quickItems?: ContextMenuItem[] | undefined;
	/**
	 * If this is set to true in entity-search-react.tsx, search members in all divisions
	 */
	searchAllDivision?: boolean;

	onSelectEntity: (entity: EntitySearchType | undefined) => void;

	/**
	 * onSearchComplete will fire when the search results have finished fetching successfully
	 * This is utilized by Jest tests to determine when a search item is clickable.
	 */
	onSearchComplete?: () => void;
	/**
	 * If this is set to true in entity-search-react.tsx, refreshMemberEntity() will fire to
	 * update the entity search with the same parameters because we expect there may be a change
	 * to the member entity
	 */
	refreshMemberEntity?: boolean;
	/**
	 * Used by `entity-search.spec.tsx` so the Entity Search in the Jest tests do not use
	 * Autocomplete components. This is required because Autocomplete relies on JQuery
	 * and JQuery is not supported in our Jest configuration
	 */
	skipAutocomplete?: boolean;
	setUpdatedSearchHistory?: (updateClientModel: MemberSearchResult) => void;
}

interface State {
	searchQuery: string;

	// Member - Advanced Search
	searchGivenNames: string;
	searchMiddleNames: string;
	searchSurname: string;
	searchFund: AutocompleteFieldModel;
	searchMember: string;
	searchClient: string;
	searchCustomerNumber: string;
	searchDateOfBirth: string;
	searchCategory: AutocompleteFieldModel;
	searchStatus: AutocompleteFieldModel;
	searchTfn: string;

	// Employer - Advanced Search
	searchEmployer: AutocompleteFieldModel;
	searchPayrollName: string;
	searchTradingName: string;
	searchInstitutionType: string;
	searchState: string;
	searchPostcode: string;
	searchAbn: string;
	searchPayrollType: string;

	isExpanded: boolean;
	isMenuOpen: boolean;
	isAdvancedSearchExpanded: boolean;
	focusedEntity: EntitySearchType | null;
	results: EntitySearchType[];
	maybeHasMoreRecords: boolean;
	status: EntitySearchStatus;
}

export class EntitySearch extends React.Component<Props, State> {
	private readonly refRootElem = React.createRef<HTMLDivElement>();

	private readonly refResultsElem = React.createRef<HTMLDivElement>();

	private readonly refInputElem = React.createRef<HTMLInputElement>();

	private readonly refAdvSearchElem = React.createRef<HTMLDivElement>();

	private readonly refFirstAdvancedSearchInput =
		React.createRef<HTMLInputElement>();

	/**
	 * If the first input on the Advanced Search is an Autocomplete component, store the input element
	 * on this reference. This is required because the `ref` attribute of the input is determined at
	 * the `autocomplete.tsx` level when the React component renders and then is passed up the chain
	 * to this level.
	 */
	private refFirstAdvancedSearchAutocomplete: HTMLInputElement;

	private readonly memberSearchApi: MemberSearchApi =
		Container.instance.get(MemberSearchApi);

	private readonly employerSearchApi: EmployerSearchApi =
		Container.instance.get(EmployerSearchApi);

	private readonly investmentManagerSearchApi: InvestmentManagerSearchApi =
		Container.instance.get(InvestmentManagerSearchApi);

	private readonly adviserSearchApi: AdviserSearchApi =
		Container.instance.get(AdviserSearchApi);

	private readonly i18n: I18N = Container.instance.get(I18N);

	private readonly settingsState: SettingsState =
		Container.instance.get(SettingsState);

	private readonly SEARCH_DEBOUNCE_DURATION: number = 500;

	private readonly memberAdvancedSearchMap = new Map<
		AdvancedSearchInputType,
		AdvancedSearchInputProps | AdvancedSearchAutocompleteProps
	>([
		// NOTE: Jake: 2019-08-27 - WEBUI-2239
		// FieldMaxLength values were defined by Niral in a Word
		// document provided in the above JIRA.
		[
			AdvancedSearchInputType.SURNAME,
			{
				title: 'D2z_Surname',
				stateProp: 'searchSurname',
				fieldMaxLength: 40,
				firstInput: true,
			},
		],
		[
			AdvancedSearchInputType.GIVEN_NAMES,
			{
				name: 'givenNames',
				title: 'D2z_Given_Names',
				stateProp: 'searchGivenNames',
				fieldMaxLength: 40,
			},
		],
		[
			AdvancedSearchInputType.MIDDLE_NAMES,
			{
				name: 'middleNames',
				title: 'D2z_Middle_Names',
				stateProp: 'searchMiddleNames',
				fieldMaxLength: 40,
			},
		],
		[
			AdvancedSearchInputType.FUND,
			{
				title: 'MDz_Fund',
				stateProp: 'searchFund',
				searchType: SearchType.FUND,
			},
		],
		[
			AdvancedSearchInputType.MEMBER,
			{
				title: 'MDz_Member',
				stateProp: 'searchMember',
				fieldMaxLength: 6,
				onChangeProperty: (value: string) =>
					this.onChangeAdvancedSearchUpper(
						value,
						AdvancedSearchInputType.MEMBER,
					),
			},
		],
		[
			AdvancedSearchInputType.CLIENT,
			{
				title: 'D2z_Client',
				stateProp: 'searchClient',
				fieldMaxLength: 17,
			},
		],
		[
			AdvancedSearchInputType.CUSTOMER_NUMBER,
			{
				title: 'MDz_Cust_No',
				stateProp: 'searchCustomerNumber',
				fieldMaxLength: 15,
			},
		],
		[
			AdvancedSearchInputType.DATE_OF_BIRTH,
			{
				name: 'dateOfBirth',
				title: 'D2d_Birth',
				stateProp: 'searchDateOfBirth',
				onChangeProperty: (value: string) =>
					this.onChangeAdvancedSearchDate(
						value,
						AdvancedSearchInputType.DATE_OF_BIRTH,
					),
			},
		],
		[
			AdvancedSearchInputType.CATEGORY,
			{
				title: 'MDz_Category',
				stateProp: 'searchCategory',
				searchType: SearchType.CATEGORY,
				resultsDropUp: true,
			},
		],
		[
			AdvancedSearchInputType.MEMBER_EMPLOYER,
			{
				title: 'MDz_Payroll',
				stateProp: 'searchEmployer',
				searchType: SearchType.PAYROLL,
				resultsDropUp: true,
			},
		],
		[
			AdvancedSearchInputType.STATUS,
			{
				title: 'MDz_Status',
				stateProp: 'searchStatus',
				searchType: SearchType.STATUS,
				displayResultsOnFocus: true,
				resultsDropUp: true,
			},
		],
	]);

	private readonly employerAdvancedSearchMap = new Map<
		AdvancedSearchInputType,
		AdvancedSearchInputProps | AdvancedSearchAutocompleteProps
	>([
		[
			AdvancedSearchInputType.PAYROLL_FUND,
			{
				title: 'COz_Fund',
				stateProp: 'searchFund',
				searchType: SearchType.FUND,
				firstInput: true,
			},
		],
		[
			AdvancedSearchInputType.EMPLOYER,
			{
				title: 'COz_Payroll',
				stateProp: 'searchEmployer',
				searchType: SearchType.PAYROLL,
			},
		],
		[
			AdvancedSearchInputType.PAYROLL_NAME,
			{
				title: 'COz_Payroll_Name',
				stateProp: 'searchPayrollName',
				fieldMaxLength: 100,
			},
		],
		[
			AdvancedSearchInputType.TRADING_NAME,
			{
				title: 'COz_Trade_Name',
				stateProp: 'searchTradingName',
				fieldMaxLength: 100,
			},
		],
		[
			AdvancedSearchInputType.INSTITUTION_TYPE,
			{
				title: 'COz_Institution',
				stateProp: 'searchInstitutionType',
				fieldMaxLength: 2,
			},
		],
		[
			AdvancedSearchInputType.STATE,
			{
				title: 'COz_Address_Line_103',
				stateProp: 'searchState',
				fieldMaxLength: 40,
			},
		],
		[
			AdvancedSearchInputType.POSTCODE,
			{
				title: 'COz_Post_Code',
				stateProp: 'searchPostcode',
				fieldMaxLength: 4,
			},
		],
		[
			AdvancedSearchInputType.ABN,
			{
				title: 'COz_Aust_Company_No',
				stateProp: 'searchAbn',
				fieldMaxLength: 14,
			},
		],
		[
			AdvancedSearchInputType.PAYROLL_TYPE,
			{
				title: 'COc_Payroll_Type',
				stateProp: 'searchPayrollType',
				fieldMaxLength: 1,
			},
		],
	]);

	private resultsPS: PerfectScrollbar;
	private advSearchPS: PerfectScrollbar;

	private focusTrap: FocusTrap;

	constructor(props) {
		super(props);
		this.state = {
			searchQuery: '',
			searchGivenNames: '',
			searchMiddleNames: '',
			searchSurname: '',
			searchFund: { value: '', description: '' },
			searchMember: '',
			searchClient: '',
			searchCustomerNumber: '',
			searchDateOfBirth: '',
			searchCategory: { value: '', description: '' },
			searchStatus: { value: '', description: '' },
			searchTfn: '',

			searchEmployer: { value: '', description: '' },
			searchPayrollName: '',
			searchTradingName: '',
			searchInstitutionType: '',
			searchState: '',
			searchPostcode: '',
			searchAbn: '',
			searchPayrollType: '',

			isExpanded: false,
			isMenuOpen: false,
			isAdvancedSearchExpanded: false,
			focusedEntity: null,
			results: [],
			maybeHasMoreRecords: false,
			status: EntitySearchStatus.DEFAULT,
		};
	}

	private readonly onClickOpenSearch = (e: React.MouseEvent): void => {
		this.openSearch();
	};

	private readonly onFocusSearchInput = (e: React.FocusEvent): void => {
		this.openSearch();
	};

	private readonly onClickActiveAccounts = (e: React.MouseEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		this.queryForMembersWithSameAccountNumber(true);
	};

	private readonly onClickExitedAccounts = (e: React.MouseEvent): void => {
		e.preventDefault();
		e.stopPropagation();
		this.queryForMembersWithSameAccountNumber(false);
	};

	private readonly queryForMembersWithSameAccountNumber = (
		active: boolean,
	): void => {
		const { focusedEntity } = this.state;
		if (focusedEntity.entityType !== EntityType.MEMBER) {
			return;
		}
		if (
			(active && focusedEntity.activeAcctNo < 1) ||
			(!active && focusedEntity.inactiveAcctNo < 1)
		) {
			return;
		}
		this.query('client:(' + focusedEntity.values.MDz_Acct_No.trim() + ')');
	};

	private readonly query = (query: string): void => {
		this.setState(
			{
				isExpanded: true,
				isAdvancedSearchExpanded: false,
				searchQuery: query,
				results: [],
				maybeHasMoreRecords: false,
			},
			this.onUpdateSearchQueryState,
		);
	};

	private readonly openSearch = (): void => {
		this.setState({
			isExpanded: true,
			isAdvancedSearchExpanded: false,
		});
	};

	public componentDidMount() {
		this.processEntityFromURLParams();
		if (this.refResultsElem.current) {
			this.resultsPS = new PerfectScrollbar(this.refResultsElem.current);
		}
		if (this.refRootElem.current) {
			this.focusTrap = new FocusTrap(this.refRootElem.current, {
				isElementAllowedOutsideOfTrap:
					this.isElementAllowedOutsideOfTrap,
			});
		}
	}

	public componentWillUnmount() {
		if (this.focusTrap) {
			this.focusTrap.disable();
			this.focusTrap = undefined;
		}
		if (this.resultsPS) {
			this.resultsPS.destroy();
		}
		if (this.advSearchPS) {
			this.advSearchPS.destroy();
		}
	}

	public componentDidUpdate(prevProps: Props, prevState: State, snapshot) {
		if (this.props.refreshMemberEntity) {
			this.refreshMemberEntity();
			this.refreshMemberSearchHistory();
		}

		// NOTE: Jake: 2019-07-01
		// If URL parameters are changed, re-process
		if (this.props.urlParams !== prevProps.urlParams) {
			this.processEntityFromURLParams();
		}

		if (this.props.entityType !== prevProps.entityType) {
			this.setState({
				status: EntitySearchStatus.DEFAULT,
				searchQuery: '',
				results: [],
				maybeHasMoreRecords: false,
			});
		}

		// Handle focus trap
		if (this.focusTrap && !this.refRootElem.current) {
			this.focusTrap.disable();
			this.focusTrap = undefined;
		}
		if (this.focusTrap && this.state.isExpanded !== prevState.isExpanded) {
			if (this.state.isExpanded === true) {
				this.focusTrap.enable();
			} else {
				this.focusTrap.disable();
			}
		}

		// Focus on state changes
		if (
			this.state.isExpanded !== prevState.isExpanded &&
			this.state.isExpanded === true
		) {
			this.refInputElem.current.focus();
		}
		if (
			this.state.isAdvancedSearchExpanded !==
			prevState.isAdvancedSearchExpanded
		) {
			if (this.state.isAdvancedSearchExpanded === true) {
				// Focus first advanced search input if opened
				if (this.refFirstAdvancedSearchAutocomplete) {
					this.refFirstAdvancedSearchAutocomplete.focus();
				} else {
					this.refFirstAdvancedSearchInput.current.focus();
				}
			} else {
				// On close, go back to search input
				this.refInputElem.current.focus();
			}
		}

		// NOTE: Jake: 2019-06-26
		// Update PerfectScrollbar on any re-render. For optimal performance, Ideally we might have
		// the autocomplete list in its own component so this is only called when the autocomplete list
		// is updated.
		// Also using "this.resultsPS.update()" doesn't work here on large lists, it just breaks the scrollbar.
		// To test a large list, I simply searched "INGD".
		if (this.resultsPS) {
			this.resultsPS.destroy();
		}
		if (this.refResultsElem.current) {
			this.resultsPS = new PerfectScrollbar(this.refResultsElem.current);
		}
	}

	/**
	 * Sets the reference for the first Advanced Search input if it is an Autocomplete
	 * component. If it is not, it is set to undefined so that `refFirstAdvancedSearchInput`
	 * can be used to take focus instead.
	 */
	private readonly handleRefInputElemChange = (
		refInputElem: HTMLInputElement,
	): void => {
		this.refFirstAdvancedSearchAutocomplete = refInputElem;
	};

	private readonly isElementAllowedOutsideOfTrap = (
		el: HTMLElement,
	): boolean => {
		let isAllowed: boolean = false;
		// allow datepicker and autocomplete search results to avoid focus trap listeners
		const elementsOutsideTrap = document.querySelectorAll(
			'.flatpickr-calendar, .a-field--search__results',
		);
		for (const element of elementsOutsideTrap) {
			isAllowed = isAllowed || element.contains(el);
		}
		return isAllowed;
	};

	private onKeyDown = (e: React.KeyboardEvent): void => {
		switch (e.key) {
			case Key.F10:
				e.preventDefault();
				this.submitAdvancedSearch();
				break;
			case Key.ESCAPE:
				this.unfocusAdvanceSearch();
				break;
		}
	};

	private readonly onSubmitAdvancedSearch = (e: React.FormEvent): void => {
		e.preventDefault();
		this.submitAdvancedSearch();
	};

	private readonly submitAdvancedSearch = (): void => {
		let newSearchQuery = '';

		this.getAdvancedSearchMapForEntityType().forEach((props, type) => {
			let stateProp = this.state[props.stateProp];
			const autocompleteProps = props as AdvancedSearchAutocompleteProps;
			if (autocompleteProps && autocompleteProps.searchType) {
				stateProp = stateProp.value;
			}

			if (stateProp) {
				if (type === AdvancedSearchInputType.DATE_OF_BIRTH) {
					// NOTE: Jake: 2019-07-12 - WEBUI-2239
					// Strip time component from query
					stateProp = stateProp.split('T', 2)[0];
				}
				newSearchQuery = EntitySearch.addSearchTerm(
					newSearchQuery,
					type,
					stateProp,
				).trim();
			}
		});

		this.query(newSearchQuery);
	};

	private static addSearchTerm(
		query: string,
		name: string,
		value: string,
	): string {
		// NOTE: Jake: 2019-07-16 - WEBUI-2239
		// We used to only include parens when they were needed, however after
		// chatting with Umit, we will always add them on for now as that "teaches"
		// the user how to add spaces between words.
		const needsParentheses = true; // (value.includes(' ') || value.includes(':'));
		query += ' ' + name + ':';
		if (needsParentheses) {
			query += '(';
		}
		query += value.trim();
		if (needsParentheses) {
			query += ')';
		}
		return query;
	}

	private readonly onChangeSearchText = (
		e: React.ChangeEvent<HTMLInputElement>,
	): void => {
		const value = e.target.value;
		this.setState(
			{
				searchQuery: value,
			},
			this.onUpdateSearchQueryState,
		);
	};

	private onUpdateSearchQueryState = (): void => {
		if (!this.state.searchQuery.trim()) {
			this.setState({
				status: EntitySearchStatus.DEFAULT,
			});
			return;
		}
		// NOTE: Jake: 2019-06-26
		// When async update of searchQuery occurs, trigger autocomplete query
		this.beginSearch();
	};

	/**
	 * onLoadEntityfromURL will fire if an entity was loaded successfully from URL parameters
	 */
	private readonly onLoadEntityfromURL = (entity: EntitySearchType): void => {
		this.setEntity(entity);
	};

	private readonly processEntityFromURLParams = (): void => {
		const entityType = this.props.entityType;
		switch (entityType) {
			case EntityType.EMPTY:
				// NOTE(Jae): 2020-02-20
				// Entity type "EMPTY" does not support the search bar.
				return;

			case EntityType.MEMBER: {
				const params = this.props.urlParams;
				const memberParam = params.member;
				const fundParam = params.fund;
				if (!memberParam || !fundParam) {
					this.clearEntity();
					return;
				}
				Logger.log(
					`Getting member information from API. member = "${memberParam}", fund = "${fundParam}"`,
				);
				this.setState({
					status: EntitySearchStatus.RETRIEVING_DATA,
				});
				this.memberSearchApi
					.getMember(fundParam, memberParam, false)
					.then((member) => {
						if (!member) {
							throw new Error(
								'Unexpected error. Member is missing from request.',
							);
						}
						this.onLoadEntityfromURL(member as EntitySearchType);
					})
					.catch((error) => {
						this.onErrorProcessingEntity('Member', error);
					});
				break;
			}

			case EntityType.EMPLOYER: {
				const params = this.props.urlParams;
				const employerParam = params.employer;
				const fundParam = params.fund;
				if (!employerParam || !fundParam) {
					this.clearEntity();
					return;
				}
				Logger.log(
					`Getting employer information from API. employer = "${employerParam}", fund = "${fundParam}"`,
				);
				this.setState({
					status: EntitySearchStatus.RETRIEVING_DATA,
				});
				this.employerSearchApi
					.getEmployer(fundParam, employerParam)
					.then((employer) => {
						if (!employer) {
							throw new Error(
								'Unexpected error. Employer is missing from request.',
							);
						}
						this.onLoadEntityfromURL(employer);
					})
					.catch((error) => {
						this.onErrorProcessingEntity('Employer', error);
					});
				break;
			}

			case EntityType.INVESTMENT_MANAGER: {
				const params = this.props.urlParams;
				const invCodeParam = params[CommonURLParam.INVESTMENT_MANAGER];
				if (!invCodeParam) {
					this.clearEntity();
					return;
				}
				Logger.log(
					`Getting investment manager information from API. code = "${invCodeParam}"`,
				);
				this.setState({
					status: EntitySearchStatus.RETRIEVING_DATA,
				});
				this.investmentManagerSearchApi
					.getInvestmentManager(invCodeParam)
					.then((investmentManager) => {
						if (!investmentManager) {
							throw new Error(
								'Unexpected error. Investment Manager is missing from request.',
							);
						}
						this.onLoadEntityfromURL(investmentManager);
					})
					.catch((error) => {
						this.onErrorProcessingEntity(
							'InvestmentManager',
							error,
						);
					});
				break;
			}

			case EntityType.ADVISER: {
				const params = this.props.urlParams;
				const adviserParam = params[CommonURLParam.ADVISER];
				if (!adviserParam) {
					this.clearEntity();
					return;
				}
				Logger.log(
					`Getting adviser information from API. code = "${adviserParam}"`,
				);
				this.setState({
					status: EntitySearchStatus.RETRIEVING_DATA,
				});

				this.adviserSearchApi
					.getAdviser(adviserParam)
					.then((adviser) => {
						if (!adviser) {
							throw new Error(
								'Unexpected error. Adviser is missing from request.',
							);
						}
						this.onLoadEntityfromURL(adviser);
					})
					.catch((error) => {
						this.onErrorProcessingEntity('Adviser', error);
					});
				break;
			}

			default:
				const exhaustiveCheck: never = entityType;
				throw new Error(
					`Entity type "${entityType}" is currently not supported.`,
				);
		}
	};

	private readonly onErrorProcessingEntity = (
		context: string,
		error,
	): void => {
		const reason = context + ': ' + FetchUtils.normalizeError(error);
		Notification.error(reason);
		Logger.error(reason);
		this.clearEntity();
	};

	private readonly refreshMemberEntity = (): void => {
		const params = this.props.urlParams;
		const memberParam = params.member;
		const fundParam = params.fund;
		if (!memberParam || !fundParam) {
			this.clearEntity();
			return;
		}
		Logger.log(
			`Getting member information from API. member = "${memberParam}", fund = "${fundParam}"`,
		);
		this.memberSearchApi
			.getMember(fundParam, memberParam, false)
			.then((member) => {
				if (!member) {
					throw new Error(
						'Unexpected error. Member is missing from request.',
					);
				}
				this.setState({ focusedEntity: member });
			})
			.catch((error) => {
				this.onErrorProcessingEntity('Member', error);
			});
	};

	// refresh SearchHistory when member details update
	private readonly refreshMemberSearchHistory = (): void => {
		const params = this.props.urlParams;
		const memberParam = params.member;
		const fundParam = params.fund;
		const clientParam = params.client;
		if (!memberParam || !fundParam || !clientParam) {
			return;
		}
		const queryString = `fund:(${fundParam}) member:(${memberParam}) client:(${clientParam.trim()})`;
		this.memberSearchApi
			.getSearchResults(queryString)
			.then((response) => {
				if (
					!response ||
					!response.data ||
					response.data.length !== 1 ||
					!response.data[0]
				) {
					throw new Error(
						`Can not get search result for member = "${memberParam}", fund = "${fundParam}" client = "${clientParam}" when updating member search history`,
					);
				}
				this.props.setUpdatedSearchHistory(response.data[0]);
			})
			.catch((error) => {
				this.onErrorProcessingEntity('Member', error);
			});
	};

	/**
	 * Prepare search query then make API request
	 */
	private readonly beginSearch = debounce(() => {
		const query = this.state.searchQuery.trim();
		if (!query) {
			return;
		}
		const entityType = this.props.entityType;
		if (!entityType || entityType === EntityType.EMPTY) {
			return;
		}
		Logger.log(`Searching for ${entityType}: "${query}"`);
		this.setState({
			results: [],
			maybeHasMoreRecords: false,
			status: EntitySearchStatus.IN_PROGRESS,
		});

		switch (entityType) {
			case EntityType.MEMBER: {
				this.memberSearchApi
					.getSearchResults(query, this.props.searchAllDivision)
					.then((response) => {
						this.setState({
							status: EntitySearchStatus.SEARCH_SUCCESS,
							// TODO: Jake: 2019-06-26
							// Look into why this throw type errors and doesn't
							// just work.
							results: response.data as EntitySearchType[],
							maybeHasMoreRecords: response.maybeHasMoreRecords,
						});
						this.onSearchComplete();
					})
					.catch(this.onSearchCatch);
				break;
			}

			case EntityType.EMPLOYER: {
				this.employerSearchApi
					.getSearchResults(query)
					.then((response) => {
						this.setState({
							status: EntitySearchStatus.SEARCH_SUCCESS,
							results: response.data,
							maybeHasMoreRecords: response.maybeHasMoreRecords,
						});
						this.onSearchComplete();
					})
					.catch(this.onSearchCatch);
				break;
			}

			case EntityType.INVESTMENT_MANAGER: {
				this.investmentManagerSearchApi
					.getSearchResults(query)
					.then((response) => {
						this.setState({
							status: EntitySearchStatus.SEARCH_SUCCESS,
							results: response.data,
							maybeHasMoreRecords: response.maybeHasMoreRecords,
						});
						this.onSearchComplete();
					})
					.catch(this.onSearchCatch);
				break;
			}

			case EntityType.ADVISER: {
				this.adviserSearchApi
					.getSearchResults(query)
					.then((response) => {
						this.setState({
							status: EntitySearchStatus.SEARCH_SUCCESS,
							results: response.data,
							maybeHasMoreRecords: response.maybeHasMoreRecords,
						});
						this.onSearchComplete();
					})
					.catch(this.onSearchCatch);
				break;
			}

			default:
				const exhaustiveCheck: never = entityType;
				throw new Error(
					`Entity type "${entityType}" is currently not supported.`,
				);
		}
	}, this.SEARCH_DEBOUNCE_DURATION);

	private readonly onSearchCatch = (error): void => {
		const message = FetchUtils.normalizeError(error);
		Notification.error(
			this.i18n.tr('entity-search.error-with-results', { message }),
		);
		Logger.error(message);
	};

	private readonly onSearchComplete = (): void => {
		if (this.props.onSearchComplete) {
			this.props.onSearchComplete();
		}
	};

	/**
	 * onSelectEntity will fire when an entity is selected from the entiy search autocomplete listing
	 */
	private readonly onSelectEntity = (entity: EntitySearchType): void => {
		const { focusedEntity } = this.state;
		if (
			EntityTypeUtils.getUniqueID(focusedEntity) ===
			EntityTypeUtils.getUniqueID(entity)
		) {
			// NOTE: Jake: 2019-10-17 - WEBUI-2550
			// Don't trigger any entity change logic if we click the same entity
			this.unfocusSearch();
			return;
		}
		this.setEntity(entity);

		// NOTE: Jake: 2019-06-26 - WEBUI-2196
		// We use this callback to trigger Aurelia router logic
		this.props.onSelectEntity(entity);
	};

	private setEntity = (entity: EntitySearchType): void => {
		if (!entity) {
			throw new Error(
				'Unexpected error. Cannot pass undefined or null entity.',
			);
		}

		// Set focused entity
		this.setState({
			focusedEntity: entity,
			status: EntitySearchStatus.DEFAULT,
		});
		this.unfocusSearch();
	};

	private readonly onClickDeselectEntity = (event) => {
		this.unfocusSearch();
		this.clearEntity();
	};

	private readonly clearEntity = (): void => {
		if (!this.state.focusedEntity) {
			// NOTE: Jake: 2019-07-09 - WEBUI-2196
			// Avoid triggering onSelectEntity and updating state
			// if we don't have a focused entity.
			return;
		}
		Logger.log('Clearing focused entity');
		this.setState({
			searchQuery: '',
			focusedEntity: null,
			results: [],
			status: EntitySearchStatus.DEFAULT,
		});
		this.unfocusSearch();

		// NOTE: Jake: 2019-06-26 - WEBUI-2196
		// We use this callback to trigger Aurelia router logic
		this.props.onSelectEntity(undefined);
	};

	/**
	 * Keydown events on the input element
	 */
	private readonly handleInputKeydown = (keyEvent: React.KeyboardEvent) =>
		this.handleKeydown(
			keyEvent,
			[Key.ESCAPE, Key.ARROW_DOWN],
			(e: React.KeyboardEvent) => {
				switch (e.key) {
					case Key.ESCAPE:
						this.unfocusSearch();
						break;

					case Key.ARROW_DOWN:
						// NOTE: Jake: 2019-06-26 - WEBUI-2196
						// This is not very idomatic React as this component has knowledge
						// of elmemnts inside a seperate component. However for now, the aim is to keep
						// the code looking as close to the Aurelia version as possible.
						const firstResultElem: HTMLElement | null =
							this.refResultsElem.current.querySelector(
								'button[data-index="0"]',
							);
						if (firstResultElem) {
							firstResultElem.focus();
						}
						break;
				}
			},
		);

	/**
	 * Keydown events on the search results. Used in `entity-search-result.html`
	 */
	private onResultKeyDown = (keyEvent: React.KeyboardEvent) =>
		this.handleKeydown(
			keyEvent,
			[Key.ARROW_DOWN, Key.ARROW_UP, Key.ENTER, Key.ESCAPE],
			(e: React.KeyboardEvent) => {
				const key = e.key;
				switch (key) {
					case Key.ESCAPE:
						this.unfocusSearch();
						break;

					case Key.ENTER:
						if (e.target instanceof HTMLElement) {
							e.target.click();
						}
						break;

					case Key.ARROW_DOWN:
					case Key.ARROW_UP:
						if (!(e.target instanceof HTMLElement)) {
							break;
						}
						let nextTabIndex = Number(
							e.target.getAttribute('data-index'),
						);
						if (key === Key.ARROW_DOWN) {
							nextTabIndex++;
						} else {
							nextTabIndex--;
						}

						// NOTE: Jake: 2019-06-26 - WEBUI-2196
						// This is not very idomatic React as this component has knowledge
						// of elmemnts inside a seperate component. However for now, the aim is to keep
						// the code looking as close to the Aurelia version as possible.
						const nextResultElem =
							this.refResultsElem.current.querySelector(
								'button[data-index="' + nextTabIndex + '"]',
							);

						if (
							nextResultElem &&
							nextResultElem instanceof HTMLElement
						) {
							// If next element is found, focus on that
							nextResultElem.focus();
						} else if (e.target.tabIndex === 0) {
							// If first element and trying to focus on previous, focus on input
							this.refInputElem.current.focus();
						}
						break;
				}
			},
		);

	/**
	 * Keydown event handler helper method
	 *
	 * @param {KeyboardEvent}	event			Keyboard event
	 * @param {string[]}		supportedKeys	Supported keys
	 * @param {callback}		calllback		Callback function to be executed if `event.code` is in `supportedKeys`
	 */
	private handleKeydown = (
		event: React.KeyboardEvent,
		supportedKeys: string[],
		callback: (event: React.KeyboardEvent) => void,
	) => {
		if (event && supportedKeys.indexOf(event.key) > -1) {
			Logger.log(`Handling key event for: '${event.key}'`);
			if (event.preventDefault) {
				event.preventDefault();
			}
			if (event.stopPropagation) {
				event.stopPropagation();
			}
			callback(event);
		}
	};

	private readonly onClickOpenAdvancedSearch = (e: React.MouseEvent) => {
		this.setState(
			{
				isAdvancedSearchExpanded: !this.state.isAdvancedSearchExpanded,
			},
			() => {
				if (this.refAdvSearchElem.current) {
					this.advSearchPS = new PerfectScrollbar(
						this.refAdvSearchElem.current,
					);
				}
			},
		);
	};

	private readonly onChangeAdvancedSearch = (
		value: string,
		inputType: AdvancedSearchInputType,
	) => {
		const state = {};
		state[
			this.getAdvancedSearchMapForEntityType().get(inputType).stateProp
		] = value;
		this.setState(state);
	};

	private readonly onChangeAdvancedSearchDate = (
		value: string | undefined,
		inputType: AdvancedSearchInputType,
	) => {
		const state = {};
		state[
			this.getAdvancedSearchMapForEntityType().get(inputType).stateProp
		] = value;
		this.setState(state);
	};

	private readonly onChangeAdvancedSearchUpper = (
		value: string,
		inputType: AdvancedSearchInputType,
	) => {
		const state = {};
		state[
			this.getAdvancedSearchMapForEntityType().get(inputType).stateProp
		] = value.toUpperCase();
		this.setState(state);
	};

	/**
	 * Fires when a search result of an Autocomplete field within Advanced Search e.g. Fund, Category, is selected.
	 * Differs from other Advanced Search fields in that State is only updated on Select, not on Change.
	 */
	private readonly onSelectAdvancedSearchAutocomplete = (
		record: { [columnName: string]: string },
		inputType: AdvancedSearchInputType,
	) => {
		const state = {};
		const inputProps = this.getAdvancedSearchMapForEntityType().get(
			inputType,
		) as AdvancedSearchAutocompleteProps;
		const model: AutocompleteFieldModel = { value: '', description: '' };

		if (record) {
			const searchDefinition: SearchDefinition =
				SearchDefinitionMap[inputProps.searchType];
			// e.g. FRz_Fund
			const rowIdKey: string = searchDefinition.rowIdKey;
			model.value = record[rowIdKey];
			// e.g. ['FRz_Fund_Name']
			const descriptionDefinition: string[] =
				searchDefinition.descriptionDefinition;
			const descArray: string[] = descriptionDefinition.map((def) =>
				record[def] ? record[def] : '',
			);
			model.description =
				AcurityFieldUtils.descriptionFromArray(descArray);
		}

		// e.g. searchFund
		const searchProp: string = inputProps.stateProp;
		state[searchProp] = model;
		this.setState(state);
	};

	private readonly unfocusAdvanceSearch = () => {
		this.setState({
			searchQuery: '',
			isExpanded: true,
			isAdvancedSearchExpanded: false,
		});
	};

	private readonly unfocusSearch = () => {
		this.setState({
			searchQuery: '',
			isExpanded: false,
			isAdvancedSearchExpanded: false,
		});
	};

	private readonly getResultInfo = (results: EntitySearchType[]): string => {
		const { status, searchQuery, maybeHasMoreRecords } = this.state;
		const { searchHistoryList } = this.props;
		switch (status) {
			case EntitySearchStatus.DEFAULT:
				if (searchQuery === '') {
					if (searchHistoryList.length > 0) {
						return this.i18n.tr('entity-search.recent-searches');
					} else {
						return this.i18n.tr('entity-search.begin-typing');
					}
				}
				return '';
			case EntitySearchStatus.IN_PROGRESS:
				return this.i18n.tr('entity-search.searching');
			case EntitySearchStatus.SEARCH_SUCCESS:
				if (!results || results.length === 0) {
					return this.i18n.tr('entity-search.no-search-results', {
						searchQuery,
					});
				}
				if (results && results.length > 0) {
					if (maybeHasMoreRecords) {
						return this.i18n.tr(
							'entity-search.maybe-more-search-results',
							{ number: results.length },
						);
					} else {
						return this.i18n.tr('entity-search.search-results', {
							number: results.length,
						});
					}
				}
				return this.i18n.tr('entity-search.searching');
		}
		return '';
	};

	get entityIconClass(): string {
		return EntityIconClass[this.props.entityType];
	}

	get entityTypeLookupCopy(): string {
		return this.i18n.tr('entity-search.lookup', {
			entityType: this.props.entityDisplayName,
		});
	}

	private renderSupportingDetails(): JSX.Element {
		const { focusedEntity } = this.state;
		switch (focusedEntity.entityType) {
			case EntityType.MEMBER:
				return (
					<ul
						className={styles['selected-entity-supporting-details']}
					>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.status')}
							value={focusedEntity.values.MDz_Status}
							description={
								focusedEntity.fieldModel &&
								focusedEntity.fieldModel.MDz_Status
									? focusedEntity.fieldModel.MDz_Status
											.fieldDescription
									: ''
							}
						>
							{focusedEntity.status && focusedEntity.status.text && (
								<div
									className={styles['report-status']}
									style={{
										color: focusedEntity.status.textColor,
										backgroundColor:
											focusedEntity.status
												.backgroundColor,
										borderColor:
											focusedEntity.status.borderColor,
									}}
								>
									<p
										className={styles['report-copy']}
										title={focusedEntity.status.text}
									>
										{focusedEntity.status.text}
									</p>
								</div>
							)}
						</EntitySearchSupportingDetailsItem>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.category')}
							value={focusedEntity.values.MDz_Category}
							description={focusedEntity.values.CTz_Description}
						/>
					</ul>
				);
				break;

			case EntityType.EMPLOYER:
				return (
					<ul
						className={styles['selected-entity-supporting-details']}
					>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.fund')}
							value={focusedEntity.values.COz_Fund}
						/>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.abn')}
							value={focusedEntity.values.COz_Aust_Company_No}
						/>
					</ul>
				);
				break;

			case EntityType.INVESTMENT_MANAGER:
				return (
					<ul
						className={styles['selected-entity-supporting-details']}
					>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.invest-group')}
							value={focusedEntity.values.IMz_Group}
							description={
								focusedEntity.fieldModel &&
								focusedEntity.fieldModel.IMz_Group
									? focusedEntity.fieldModel.IMz_Group
											.fieldDescription
									: ''
							}
						/>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.aas25-group')}
							value={focusedEntity.values.IMz_AAS25_Grouping}
							description={
								focusedEntity.fieldModel &&
								focusedEntity.fieldModel.IMz_AAS25_Grouping
									? focusedEntity.fieldModel
											.IMz_AAS25_Grouping.fieldDescription
									: ''
							}
						/>
						<EntitySearchSupportingDetailsItem
							label={this.i18n.tr('entity-search.status')}
							value={focusedEntity.values.IMz_Status}
							description={
								focusedEntity.fieldModel &&
								focusedEntity.fieldModel.IMz_Status
									? focusedEntity.fieldModel.IMz_Status
											.fieldDescription
									: ''
							}
						/>
					</ul>
				);
				break;

			case EntityType.ADVISER:
				// NOTE: Jake: 2019-10-01
				// No info to show for EMPTY / ADVISER.
				return undefined;

			default:
				const exhaustiveCheck: never = focusedEntity;
				break;
		}
		return undefined;
	}

	public render() {
		const entityType = this.props.entityType;
		if (entityType === undefined || entityType === EntityType.EMPTY) {
			// NOTE(Jae): 2020-02-21
			// Hide search bar.
			return <React.Fragment />;
		}
		const { quickItems } = this.props;
		const {
			isMenuOpen,
			isExpanded,
			isAdvancedSearchExpanded,
			searchQuery,
			status,
			results,
			focusedEntity,
		} = this.state;
		const searchHistoryList: EntitySearchType[] = this.props
			.searchHistoryList
			? this.props.searchHistoryList
			: [];
		const focusedEntityKey =
			focusedEntity && focusedEntity.values
				? EntityTypeUtils.getUniqueID(focusedEntity)
				: '';
		return (
			<header className="a-app__main__header">
				<div className="a-app__main__header__search">
					<div
						className={[
							styles.root,
							isExpanded ? styles['root--expanded'] : '',
							status === EntitySearchStatus.RETRIEVING_DATA
								? styles['root--loading']
								: '',
							focusedEntity && !isExpanded
								? styles['root--selected']
								: '',
						].join(' ')}
						ref={this.refRootElem}
					>
						<div className={styles.bar}>
							<div
								className={styles['entity-icon-wrapper']}
								onClick={this.onClickOpenSearch}
							>
								<span className="a-entity-search-module__entity-icon">
									<ApexIconReact name="user-circle" />
								</span>
							</div>
							<div className={styles['input-container']}>
								<label
									htmlFor="entity-search-input"
									className={utilStyles['sr-only']}
								>
									{this.entityTypeLookupCopy}
								</label>
								<input
									id="entity-search-input"
									type="text"
									className={[styles.input].join(' ')}
									value={searchQuery}
									placeholder={this.entityTypeLookupCopy}
									onChange={this.onChangeSearchText}
									onFocus={this.onFocusSearchInput}
									onKeyDown={this.handleInputKeydown}
									autoComplete="off"
									ref={this.refInputElem}
								/>
								{isExpanded &&
									(entityType === EntityType.MEMBER ||
										entityType === EntityType.EMPLOYER) && (
										<button
											className={[
												'btn',
												'btn-link',
												styles[
													'adv-search-open-button'
												],
											].join(' ')}
											type="button"
											onClick={
												this.onClickOpenAdvancedSearch
											}
											title={this.i18n.tr(
												'entity-search.advanced-search',
											)}
										>
											<i
												className="fas fa-caret-down"
												aria-hidden="true"
											/>
											<span>
												{this.i18n.tr(
													'entity-search.advanced-search',
												)}
											</span>
										</button>
									)}
							</div>
							{!isExpanded && focusedEntity && (
								<div
									className={[
										styles['selected-entity'],
										status ===
										EntitySearchStatus.RETRIEVING_DATA
											? styles['selected-entity--loading']
											: '',
									].join(' ')}
									onClick={this.onClickOpenSearch}
								>
									<EntitySearchMainDetails
										entity={focusedEntity}
									>
										{focusedEntity &&
											focusedEntity.entityType ===
												EntityType.MEMBER && (
												<React.Fragment>
													{focusedEntity.activeAcctNo >=
														0 && (
														<button
															type="button"
															className={
																styles[
																	'orb-active-user'
																]
															}
															title={this.i18n.tr(
																'entity-search.active-accounts',
																{
																	number: focusedEntity.activeAcctNo,
																},
															)}
															onClick={
																this
																	.onClickActiveAccounts
															}
														>
															<span aria-hidden="true">
																{
																	focusedEntity.activeAcctNo
																}
															</span>
															<span
																className={
																	utilStyles[
																		'sr-only'
																	]
																}
															>
																{this.i18n.tr(
																	'entity-search.active-accounts',
																	{
																		number: focusedEntity.activeAcctNo,
																	},
																)}
															</span>
														</button>
													)}
													{focusedEntity.inactiveAcctNo >=
														0 && (
														<button
															type="button"
															className={
																styles[
																	'orb-exited-user'
																]
															}
															title={this.i18n.tr(
																'entity-search.exited-accounts',
																{
																	number: focusedEntity.inactiveAcctNo,
																},
															)}
															onClick={
																this
																	.onClickExitedAccounts
															}
														>
															<span aria-hidden="true">
																{
																	focusedEntity.inactiveAcctNo
																}
															</span>
															<span
																className={
																	utilStyles[
																		'sr-only'
																	]
																}
															>
																{this.i18n.tr(
																	'entity-search.exited-accounts',
																	{
																		number: focusedEntity.inactiveAcctNo,
																	},
																)}
															</span>
														</button>
													)}
													{status ===
														EntitySearchStatus.RETRIEVING_DATA && (
														<span>
															{
																// NOTE: Jake: 2019-10-17 - WEBUI-2580
																// Give the user some feedback in the UI
																// that it's working to retrieve more data.
															}
															<i className="fas fa-circle-notch fa-spin" />
														</span>
													)}
												</React.Fragment>
											)}
									</EntitySearchMainDetails>
									{this.renderSupportingDetails()}
									{quickItems && quickItems.length > 0 && (
										<div className={styles['context-menu']}>
											<ContextMenuButton
												items={quickItems}
											/>
										</div>
									)}
								</div>
							)}
							{!isExpanded && focusedEntity && (
								<button
									type="button"
									className={styles['deselect-entity']}
									onClick={this.onClickDeselectEntity}
								>
									<i
										aria-hidden="true"
										className={[
											'fas',
											'fa-times-circle',
											styles['deselect-entity-icon'],
										].join(' ')}
									/>
								</button>
							)}
						</div>
						{isAdvancedSearchExpanded && (
							<section
								className={styles['adv-search']}
								ref={this.refAdvSearchElem}
							>
								<form
									id="advanced-search-form"
									method="get"
									onSubmit={this.onSubmitAdvancedSearch}
									onKeyDown={this.onKeyDown}
								>
									<h2 className={styles['adv-search-title']}>
										Advanced Search
									</h2>
									{this.renderAdvancedSearch()}
									<button
										type="submit"
										className="i-btn btn btn-primary"
									>
										Search
									</button>
								</form>
							</section>
						)}
						<div
							className={[
								styles['result-list-container'],
								isExpanded
									? styles['result-list-container--is-active']
									: '',
							].join(' ')}
							ref={this.refResultsElem}
						>
							{!isAdvancedSearchExpanded && (
								<p className={styles['result-list-info']}>
									{this.getResultInfo(results)}
								</p>
							)}
							{!isAdvancedSearchExpanded && (
								<ul className={styles['result-list']}>
									{searchQuery === '' &&
										searchHistoryList.length > 0 &&
										searchHistoryList.map((item, index) => {
											return (
												<EntitySearchResult
													key={EntityTypeUtils.getUniqueID(
														item,
													)}
													index={index}
													data={item}
													onClick={
														this.onSelectEntity
													}
													onKeyDown={
														this.onResultKeyDown
													}
												/>
											);
										})}
									{searchQuery !== '' &&
										results &&
										results.length > 0 &&
										results.map((item, index) => {
											return (
												<EntitySearchResult
													key={EntityTypeUtils.getUniqueID(
														item,
													)}
													index={index}
													data={item}
													onClick={
														this.onSelectEntity
													}
													onKeyDown={
														this.onResultKeyDown
													}
												/>
											);
										})}
								</ul>
							)}
						</div>
						<div
							className={
								styles.overlay +
								(isExpanded
									? ' ' + styles['overlay--is-active']
									: '')
							}
							onClick={this.unfocusSearch}
						/>
					</div>
				</div>
			</header>
		);
	}

	private renderAdvancedSearch(): JSX.Element {
		const { skipAutocomplete } = this.props;
		const searchInputArray = Array.from(
			this.getAdvancedSearchMapForEntityType(),
			([type, props]) => ({ type, props }),
		);

		return (
			<React.Fragment>
				{searchInputArray.map((input) => {
					const autocompleteProps =
						input.props as AdvancedSearchAutocompleteProps;
					if (input.type === AdvancedSearchInputType.DATE_OF_BIRTH) {
						return (
							<EntitySearchDatepicker
								key={input.type}
								name={input.props.name}
								label={this.i18n.tr(
									'entity-search.' + input.type,
								)}
								title={input.props.title}
								onChange={input.props.onChangeProperty}
								value={this.state[input.props.stateProp]}
							/>
						);
					} else if (
						autocompleteProps &&
						autocompleteProps.searchType &&
						!skipAutocomplete
					) {
						const searchDefinition: SearchDefinition =
							SearchDefinitionMap[autocompleteProps.searchType];
						const columnHeaders: AutocompleteColumnHeader[] =
							searchDefinition.columnDefinition.map(
								(columnDef) => {
									return {
										columnName: columnDef.name,
										display: columnDef.label,
									};
								},
							);

						return (
							<EntitySearchAutocomplete
								key={input.type}
								name={input.type}
								label={this.i18n.tr(
									'entity-search.' + input.type,
								)}
								title={autocompleteProps.title}
								columnName={searchDefinition.rowIdKey}
								columnHeaders={columnHeaders}
								remoteOptions={{
									name: autocompleteProps.title,
									searchDefinition,
								}}
								onSelect={(record?: {
									[columnName: string]: string;
								}) =>
									this.onSelectAdvancedSearchAutocomplete(
										record,
										input.type,
									)
								}
								displayResultsOnFocus={
									autocompleteProps.displayResultsOnFocus
								}
								value={
									this.state[autocompleteProps.stateProp]
										.value
								}
								description={
									this.state[autocompleteProps.stateProp]
										.description
								}
								handleRefInputElemChange={(
									refInputElem: HTMLInputElement,
								) =>
									autocompleteProps.firstInput
										? this.handleRefInputElemChange(
												refInputElem,
										  )
										: undefined
								}
								resultsDropUp={autocompleteProps.resultsDropUp}
							/>
						);
					} else {
						return (
							<EntitySearchInput
								key={input.type}
								name={
									input.props.name
										? input.props.name
										: input.type
								}
								label={this.i18n.tr(
									'entity-search.' + input.type,
								)}
								title={input.props.title}
								onChange={
									input.props.onChangeProperty
										? input.props.onChangeProperty
										: (value: string) =>
												this.onChangeAdvancedSearch(
													value,
													input.type,
												)
								}
								value={this.state[input.props.stateProp]}
								maxLength={
									input.props.fieldMaxLength
										? input.props.fieldMaxLength
										: undefined
								}
								inputRef={
									input.props.firstInput
										? this.refFirstAdvancedSearchInput
										: undefined
								}
							/>
						);
					}
				})}
			</React.Fragment>
		);
	}

	private getAdvancedSearchMapForEntityType(): Map<
		AdvancedSearchInputType,
		AdvancedSearchInputProps | AdvancedSearchAutocompleteProps
	> {
		const { entityType } = this.props;
		switch (entityType) {
			case EntityType.MEMBER:
				const advancedSearchMap = this.memberAdvancedSearchMap;
				if (
					this.settingsState.state.user &&
					this.settingsState.state.user.isTfnMatchAllowed
				) {
					advancedSearchMap.set(AdvancedSearchInputType.TFN, {
						title: 'D2z_Tax_File_No',
						stateProp: 'searchTfn',
						fieldMaxLength: 11,
					});
				}
				return advancedSearchMap;
			case EntityType.EMPLOYER:
				return this.employerAdvancedSearchMap;
			default:
				throw new Error(
					`Advanced Search is currently not supported for entity type "${entityType}".`,
				);
		}
	}
}
